import React from 'react';

import CustomMultiSelect from 'src/components/MessengerSettings/CustomMultiSelect';
import { chatgenieAPI } from 'src/config/api';
import RadioGroup from 'src/pages/DeploymentCenter/components/RadioGroup';
import { Button, Checkbox, RadioButton } from '@Thread-Magic/jasmine';
import AutoExpandTextarea from 'src/components/AutoExpandTextarea';
import InfoCard from 'src/components/InfoCard';
import Switcher from 'src/components/Switcher';
import useToast from 'src/hooks/useToast';
import SimpleInput from 'src/components/SimpleInput';
import PageHolder from 'src/components/PageHolder';
import useTabs from './Tabs/hook';
import {
  AgentHeader,
  AgentSettingsBox,
  AgentSettingsBoxContent,
  AgentSettingsBoxHeader,
  DeactivationTimeoutWrapper,
  SettingsWrapper
} from './style';

const getCompanies = (query, page) =>
  chatgenieAPI.getCompanies({ query, page, normalizer: res => res.data });

const usageTypes = [
  {
    value: 'all',
    label: 'Use for chat and email'
  },
  {
    value: 'chat',
    label: 'Use only for chat'
  },
  {
    value: 'email',
    label: 'Use only for email'
  }
];

const Settings = () => {
  const { isLoading, settings, updateAgentSettings, saveAgentSettings, isUpdating } = useTabs();
  const { toast } = useToast();

  const getBoards = (query, page) =>
    chatgenieAPI.getBoards({ query, page, normalizer: res => res.data });

  const handleTriageInstructionChange = e => {
    const { value } = e.target;
    if (settings.defaultTriageEnabled && !value) {
      toast.error('The Instructions field is required when default triage is enabled');
      return;
    }
    updateAgentSettings({
      defaultTriageInstructions: value
    });
  };

  if (isLoading) {
    return <PageHolder fullHeight isLoading />;
  }

  return (
    <SettingsWrapper>
      <AgentHeader>
        <div>
          <h5>Settings</h5>
        </div>
      </AgentHeader>
      <AgentSettingsBox>
        <AgentSettingsBoxHeader>
          <h6>Enable Magic Agents </h6>
          <Switcher
            id="is-magic-agent-enabled"
            checked={settings.enabled ?? false}
            onChange={() =>
              updateAgentSettings({
                enabled: !settings.enabled
              })
            }
            width={50}
            controlText={{ checked: '', unchecked: '' }}
            isLoading={isLoading}
          />
        </AgentSettingsBoxHeader>
        <AgentSettingsBoxContent>
          <div>
            <p />
            <RadioGroup
              value={settings.isAllClients ? 'all' : 'specific'}
              options={[
                { label: 'All managed clients', value: 'all' },
                { label: 'Specific clients', value: 'specific' }
              ]}
              onChange={value => {
                const isAllClients = value === 'all';
                updateAgentSettings({
                  isAllClients
                });
              }}
            />
            {!settings.isAllClients && (
              <CustomMultiSelect
                name="included-customers"
                value={settings?.customerActivations}
                options={getCompanies}
                onChange={option => updateAgentSettings({ customerActivations: option })}
                componentType="paginate"
                isLoading={isLoading}
              />
            )}
          </div>

          <div>
            <p>Exclude boards</p>
            <CustomMultiSelect
              name="excluded-boards"
              value={settings?.boardExclusions}
              options={getBoards}
              onChange={option => updateAgentSettings({ boardExclusions: option })}
              componentType="paginate"
              isLoading={isLoading}
            />
          </div>
          <div>
            <p>Choose type of support</p>
            {usageTypes.map(({ value, label }) => (
              <RadioButton
                onChange={e => updateAgentSettings({ usageType: e.target.value })}
                id="magic-agent-usage-type"
                checked={settings?.usageType === value}
                value={value}
                key={value}
              >
                {label}
              </RadioButton>
            ))}
          </div>
          <div>
            <p>When there is no response from contact</p>
            <DeactivationTimeoutWrapper>
              Pass to technician after{' '}
              <SimpleInput
                value={settings.deactivationTimeout || ''}
                type="number"
                onChange={e => {
                  const { value } = e.target;
                  updateAgentSettings({ deactivationTimeout: value });
                }}
              />{' '}
              minutes
            </DeactivationTimeoutWrapper>
          </div>
          <div>
            <p>How should Magic Agent handle status?</p>
            <Checkbox
              id="status-handling"
              checked={settings.autoSetInProgress}
              onChange={() =>
                updateAgentSettings({ autoSetInProgress: !settings.autoSetInProgress })
              }
            >
              Update the status to “In Progress” when Magic Agent triages the thread
            </Checkbox>
          </div>

          <div>
            <p>Rerun Magic AI actions</p>
            <Checkbox
              id="re-run-action"
              checked={settings.rerunMagicActions}
              onChange={() =>
                updateAgentSettings({ rerunMagicActions: !settings.rerunMagicActions })
              }
            >
              Rerun Auto-prioritization, Auto-categorization & Magic Title when thread is triaged
            </Checkbox>
          </div>
        </AgentSettingsBoxContent>
      </AgentSettingsBox>
      <AgentSettingsBox>
        <AgentSettingsBoxHeader>
          <h6>Enable AI triaging when there is not enough information</h6>
          <Switcher
            id="default-triage"
            width={50}
            checked={!!settings.defaultTriageEnabled}
            onChange={() => {
              const isDefaultTriageEnabled = !settings.defaultTriageEnabled;
              const defaultTriageActivationType =
                isDefaultTriageEnabled && !settings.defaultTriageActivationType
                  ? 'all'
                  : settings.defaultTriageActivationType;
              updateAgentSettings({
                defaultTriageEnabled: isDefaultTriageEnabled,
                defaultTriageActivationType
              });
            }}
            controlText={{ checked: '', unchecked: '' }}
          />
        </AgentSettingsBoxHeader>
        {settings.defaultTriageEnabled && (
          <>
            <InfoCard marginTop={10}>
              Magic agents will triage any new request that comes in, by asking a few default
              questions.
            </InfoCard>
            <AgentSettingsBoxContent>
              <div>
                <p>Instructions for Magic Agents to follow</p>
                <AutoExpandTextarea
                  value={settings.defaultTriageInstructions || ''}
                  placeholder="Write instructions here..."
                  onChange={handleTriageInstructionChange}
                  applyDefaultBorder
                  maxHeight={300}
                />
              </div>
            </AgentSettingsBoxContent>
          </>
        )}
      </AgentSettingsBox>
      <div>
        <Button size="large" isLoader={isUpdating} onClick={saveAgentSettings}>
          Save
        </Button>
      </div>
    </SettingsWrapper>
  );
};

export default Settings;
